import { useContext, useState } from "react";
import AutopilotCuratedSearchContext from "../../../contexts/AutopilotCuratedSearchContext";
import { CursorArrowRippleIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import CuratedSearchCardHeader from "../CuratedSearchCard/CuratedSearchCardHeader";
import CuratedSearchPromptEditDescription from "./CuratedSearchPromptEdit/CuratedSearchPromptEditDescription";
import CiroButton, { CiroButtonStyleEnum } from "../../shared/CiroButton";
import SkeletonLoading from "../../shared/SkeletonLoading";
import SpecificCompaniesPromptSection from "./CuratedSearchPromptEdit/SpecificCompaniesPromptSection";
import SuggestedPrompts from "./CuratedSearchPromptEdit/SuggestedPrompts";
import SearchPowerUpsDropdown from "./SearchPowerUps/SearchPowerUpsDropdown";
import CiroCard from "../../shared/CiroCard";

const HEADER_ICON = (
  <CursorArrowRippleIcon
    className={classNames("w-6", "h-6", "text-orange-500")}
  />
);

interface ICuratedSearchPromptSection {
  selectedPrompt?: string;
  setSelectedPrompt: (prompt: string) => void;
  startedGeneratingFilters: boolean;
  setStartedGeneratingFilters: (value: boolean) => void;
  suggestedPrompts?: string[];
  loading: boolean;
  isRemixStep?: boolean;
  handleSubmit: () => void;
}

const CuratedSearchPromptSection = ({
  selectedPrompt,
  setSelectedPrompt,
  startedGeneratingFilters,
  suggestedPrompts,
  loading,
  isRemixStep,
  handleSubmit,
}: ICuratedSearchPromptSection) => {
  const { specificCompanies, setSpecificCompanies } = useContext(
    AutopilotCuratedSearchContext,
  );

  const [error, setError] = useState<string | null>(null);
  const headerText = isRemixStep ? "Improve Results" : "Search";
  const promptPlaceholder = isRemixStep
    ? "How would you like to improve your search?"
    : "Enter a description of your ideal customer";

  const handlePromptChange = (prompt: string) => {
    setSelectedPrompt(prompt);

    if (prompt) {
      setError("");
    }
  };

  if (loading) {
    return (
      <CiroCard size="small">
        <CuratedSearchCardHeader
          headerText={headerText}
          headerIcon={HEADER_ICON}
        />
        <SkeletonLoading numSkeletons={5} skeletonHeight="1.5rem" />
      </CiroCard>
    );
  }

  if (startedGeneratingFilters) {
    return null;
  }

  return (
    <CiroCard size="small">
      <div className={classNames("flex", "flex-col", "gap-3")}>
        <CuratedSearchCardHeader
          headerText={headerText}
          headerIcon={HEADER_ICON}
          actionComponent={
            !isRemixStep && (
              <SearchPowerUpsDropdown
                specificCompanies={specificCompanies}
                setSpecificCompanies={setSpecificCompanies}
              />
            )
          }
        />

        <div>
          <div className={classNames("flex", "flex-col", "gap-4", "pt-4")}>
            <div className={classNames("flex", "flex-col", "gap-2")}>
              <CuratedSearchPromptEditDescription
                error={error}
                setError={setError}
                prompt={selectedPrompt}
                setPrompt={handlePromptChange}
                placeholder={promptPlaceholder}
              />
              {(!isRemixStep || specificCompanies.length > 0) && (
                <SpecificCompaniesPromptSection
                  specificCompanies={specificCompanies}
                  setSpecificCompanies={setSpecificCompanies}
                />
              )}
            </div>
            {suggestedPrompts && suggestedPrompts.length > 0 && (
              <SuggestedPrompts
                text={
                  isRemixStep
                    ? "Or pick one of the feedback suggestions:"
                    : "Or pick a suggested search:"
                }
                prompts={suggestedPrompts}
                onSelectPrompt={handlePromptChange}
              />
            )}
          </div>
          <div
            className={classNames(
              "flex",
              "flex-row",
              "justify-end",
              "gap-2",
              "pt-4",
            )}
          >
            <CiroButton
              analyticsField="Apply prompt updates to filters"
              style={CiroButtonStyleEnum.LOUD}
              disabled={!selectedPrompt}
              onClick={handleSubmit}
            >
              {isRemixStep ? "Improve results" : "Let's go"}
            </CiroButton>
          </div>
        </div>
      </div>
    </CiroCard>
  );
};

export default CuratedSearchPromptSection;
